import { useMemo } from "react";
import { useAccess } from "./AccessProvider";
import { slugForRoute } from "./navSlugs";
import type { NavEntry, NavItem } from "@/app/constants";

/**
 * Filters nav to what the user may actually see.
 *
 * An entry with no mapped slug is KEPT rather than hidden. That is deliberate
 * and is the opposite of the server's deny-by-default: on the server, an
 * unguarded route is a security hole, so refusing is right. In the nav, an
 * unmapped entry is far more likely to be a slug I forgot than a screen someone
 * should lose — and a missing nav entry is invisible to whoever is affected.
 * Failing open here surfaces the gap; failing closed would bury it.
 *
 * Sections whose children are all hidden collapse away, so no empty headings
 * are left behind.
 */
export function useNavAccess() {
  const { can, isReady } = useAccess();

  return useMemo(() => {
    const allow = (href: string) => {
      const slug = slugForRoute(href);
      if (!slug) return true;          // unmapped — see note above
      return can(slug);
    };

    const filterEntries = (entries: NavEntry[]): NavEntry[] => {
      if (!isReady) return [];         // deny while loading, no flash of nav
      const out: NavEntry[] = [];
      for (const entry of entries) {
        if (entry.kind === "section") {
          const items = entry.items.filter((i) => allow(i.href));
          if (items.length > 0) out.push({ ...entry, items });
        } else if (allow(entry.href)) {
          out.push(entry);
        }
      }
      return out;
    };

    const filterItems = (items: NavItem[]): NavItem[] =>
      isReady ? items.filter((i) => allow(i.href)) : [];

    return { filterEntries, filterItems, allow, isReady };
  }, [can, isReady]);
}
